import { useGuestProductFilterConfiguration } from '../shared-mui-components/filter/OperatorFilterConfiguration.tsx';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { makeSearchQueryWithFilters } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import {
    RequestStatus,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { HeaderCancelSaveButtons } from '../layout/components/HeaderCancelSaveButtons.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { GuestReservationConfig } from 'product-shared/product-template/ProductTemplate.ts';
import {
    Localized,
    useLanguage,
    useTranslation,
} from 'dg-web-shared/lib/Localization.tsx';
import {
    Control,
    Controller,
    FieldValues,
    Path,
    RegisterOptions,
    useForm,
    UseFormSetValue,
    UseFormWatch,
    useWatch,
} from 'react-hook-form';
import { DateTime } from 'luxon';
import { ensureIso } from 'dg-web-shared/lib/Date';
import {
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import { OperatorFormSelect } from '../ui/form/OperatorFormSelect.tsx';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { useEffect } from 'react';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { ProductTemplateZoneDescriptions } from '../product-types/OperatorProductTypesDetailModal.tsx';
import { ZoneType } from 'dg-web-shared/dto/ZoneType.ts';
import { OperatorParkingaboUsersProductsTenantRemarkLabel } from '../parkingabo-users/OperatorParkingaboUsersProductsAdd.tsx';
import { mandatoryFieldErrorMessage } from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { useOperatorProductListContext } from './OperatorProducts.tsx';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import {
    OperatorFormSubmitError,
    useOperatorReqErrorFieldMap,
} from '../utils/OperatorFormSubmitError.tsx';
import { GuestCardDownloadDialog } from './OperatorProductDetailModal.tsx';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';

interface GuestReservationAddTemplateItem {
    contractTemplateId: number;
    guestReservationConfig: GuestReservationConfig;
    zonesDescriptions: string[];
}

export function OperatorGuestReservationAdd() {
    const { activeFilters, searchText } = useGuestProductFilterConfiguration();
    const { refetchList } = useOperatorProductListContext();

    const [guestReservationTemplateState] = useServerFetch<
        GuestReservationAddTemplateItem[],
        object
    >(
        () => ({
            url: `/ui-api/operator-account/guest-product/guest-reservation-templates`,
        }),
        {},
    );

    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            backUrl={`..${makeSearchQueryWithFilters(searchText, activeFilters)}`}
            render={controller => (
                <OperatorAsyncLoadedSection
                    requestState={guestReservationTemplateState}
                    render={guestReservationTemplates => (
                        <GuestReservationContent
                            guestReservationTemplates={
                                guestReservationTemplates
                            }
                            onClose={controller.close}
                            onCreated={() => {
                                controller.setShouldConfirmBeforeLeave(false);
                                refetchList();
                            }}
                        />
                    )}
                />
            )}
        />
    );
}

function GuestReservationContent({
    guestReservationTemplates,
    onClose,
    onCreated,
}: {
    guestReservationTemplates: GuestReservationAddTemplateItem[];
    onClose: () => void;
    onCreated: () => void;
}) {
    const navigate = useNavigate();
    const { activeFilters, searchText } = useGuestProductFilterConfiguration();
    const { language } = useLanguage();
    const [guestReservationAddState, sendGuestReservationAdd] = useServerWrite<
        CreateGuestReservationPayload,
        CreateGuestReservationResponse,
        ValidationData
    >(() => ({
        url: '/ui-api/operator-account/guest-reservation/add',
    }));
    useServerSuccessEffect(guestReservationAddState, data => {
        onCreated();
        navigate(
            `../${data.contractExternalId}/detail/add-success${makeSearchQueryWithFilters(
                searchText,
                activeFilters,
            )}`,
        );
    });

    const now = DateTime.now();
    const form = useForm<GuestReservationAddFieldValues>({
        defaultValues: {
            [GuestReservationAddField.contractTemplateId]:
                guestReservationTemplates[0].contractTemplateId,
            [GuestReservationAddField.isStartNow]: true,
            [GuestReservationAddField.startDateTime]: ensureIso(now),
            [GuestReservationAddField.endDateTime]: '',
            [GuestReservationAddField.tenantRemark]: '',
        },
    });
    const { watch, setValue, control, handleSubmit } = form;
    useOperatorReqErrorFieldMap(guestReservationAddState, form);

    const startDateTimeString = useWatch({
        control,
        name: GuestReservationAddField.startDateTime,
    });
    const contractTemplateId = useWatch({
        control,
        name: GuestReservationAddField.contractTemplateId,
    });

    const guestReservationTemplate = guestReservationTemplates.find(
        t => t.contractTemplateId === contractTemplateId,
    );

    useEffect(() => {
        const startDateTime = startDateTimeString
            ? DateTime.fromISO(startDateTimeString)
            : null;
        if (
            !guestReservationTemplate ||
            !startDateTime ||
            !startDateTime.isValid
        )
            return;
        const defaultDateTime = startDateTime
            .plus({
                hour: guestReservationTemplate?.guestReservationConfig
                    .defaultDurationHours,
            })
            .startOf('hour');
        const maxDateTime = startDateTime.plus({
            hour: guestReservationTemplate?.guestReservationConfig
                .maxDurationHours,
        });
        const options = getTimeSelectionOptions(
            defaultDateTime,
            defaultDateTime,
            maxDateTime,
        );
        setValue(
            GuestReservationAddField.endDateTime,
            options.length > 0 ? ensureIso(options[0].value) : '',
        );
    }, [guestReservationTemplate, startDateTimeString, setValue]);
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    const startDateTime = startDateTimeString
        ? DateTime.fromISO(startDateTimeString)
        : now;

    if (guestReservationTemplates.length == 0 || !guestReservationTemplate) {
        return <Navigate to={'..'} />;
    }
    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={
                        <Localized
                            de="Neues Produkt"
                            fr="Nouveau produit"
                            it="Nuovo prodotto"
                            en="New product"
                        />
                    }
                    editMode
                    buttons={
                        <HeaderCancelSaveButtons
                            onCancel={onClose}
                            onSave={handleSubmit(data => {
                                sendGuestReservationAdd({
                                    ...data,
                                    startDateTime:
                                        data[
                                            GuestReservationAddField
                                                .startDateTime
                                        ]!,
                                    endDateTime:
                                        data[
                                            GuestReservationAddField.endDateTime
                                        ]!,
                                });
                            })}
                            loading={
                                guestReservationAddState.status ===
                                RequestStatus.PENDING
                            }
                        />
                    }
                />
            }
            body={
                <>
                    <OperatorFormSubmitError
                        submitState={guestReservationAddState}
                    />
                    <FormContentLayout>
                        <FormLeftColumn>
                            <OperatorFormSelect
                                readonly={guestReservationTemplates.length < 2}
                                label={
                                    <Localized
                                        de="Produkt-Typ"
                                        fr="Type de produit"
                                        it="Tipo prodotto"
                                        en="Product type"
                                    />
                                }
                                name={
                                    GuestReservationAddField.contractTemplateId
                                }
                                control={control}
                                options={guestReservationTemplates.map(
                                    guestReservationTemplate => ({
                                        display:
                                            guestReservationTemplate
                                                .guestReservationConfig
                                                .productName[language],
                                        value: guestReservationTemplate.contractTemplateId,
                                    }),
                                )}
                            />
                            <GuestReservationValidStartDateTime
                                now={now}
                                control={control}
                                watch={watch}
                                setValue={setValue}
                                maxDaysPurchaseAheadInFuture={
                                    guestReservationTemplate
                                        .guestReservationConfig
                                        .maxDaysPurchaseAheadInFuture
                                }
                            />
                            <Box>
                                <InputLabel variant="standard">
                                    <Localized
                                        de="Ende"
                                        fr="Fin"
                                        it="Fine"
                                        en="End"
                                    />
                                </InputLabel>
                                <GuestReservationTimeDropdowns
                                    name={GuestReservationAddField.endDateTime}
                                    control={control}
                                    minDateTime={startDateTime.plus({
                                        millisecond: 1,
                                    })}
                                    maxDateTime={startDateTime.plus({
                                        hour: guestReservationTemplate
                                            .guestReservationConfig
                                            .maxDurationHours,
                                    })}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: mandatoryFieldMessage,
                                        },
                                    }}
                                />
                            </Box>
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Zusatzinformationen"
                                        fr="Informations complémentaires"
                                        it="Informazioni supplementari"
                                        en="Additional information"
                                    />
                                }
                                value={
                                    guestReservationTemplate
                                        .guestReservationConfig
                                        .additionalInfo ? (
                                        <Localized
                                            {...guestReservationTemplate
                                                .guestReservationConfig
                                                .additionalInfo}
                                        />
                                    ) : (
                                        EmDash
                                    )
                                }
                            />
                            <ProductTemplateZoneDescriptions
                                allowedZoneTypes={[ZoneType.barrier_gate]}
                                zoneDescriptions={
                                    guestReservationTemplate.zonesDescriptions
                                }
                            />
                        </FormLeftColumn>
                        <FormRightColumn>
                            <Controller
                                name={GuestReservationAddField.tenantRemark}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        multiline
                                        minRows={3}
                                        maxRows={3}
                                        label={
                                            <OperatorParkingaboUsersProductsTenantRemarkLabel />
                                        }
                                        value={field.value}
                                        onChange={e => field.onChange(e)}
                                    />
                                )}
                            />
                        </FormRightColumn>
                    </FormContentLayout>
                </>
            }
        />
    );
}

function GuestReservationValidStartDateTime({
    now,
    control,
    watch,
    setValue,
    maxDaysPurchaseAheadInFuture,
}: {
    now: DateTime;
    control: Control<CreateGuestReservationPayload>;
    watch: UseFormWatch<CreateGuestReservationPayload>;
    setValue: UseFormSetValue<CreateGuestReservationPayload>;
    maxDaysPurchaseAheadInFuture: number;
}) {
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    const isStartNow = watch(GuestReservationAddField.isStartNow);
    return (
        <Box>
            <InputLabel variant="standard">
                <Localized de="Beginn" fr="Début" it="Inizio" en="Start" />
            </InputLabel>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '-8px !important',
                    marginLeft: '-12px !important',
                }}
            >
                <Switch
                    checked={isStartNow}
                    onChange={(_e, checked) => {
                        const date = checked
                            ? now
                            : now.plus({ days: 1 }).startOf('day');
                        setValue(GuestReservationAddField.isStartNow, checked);
                        setValue(
                            GuestReservationAddField.startDateTime,
                            ensureIso(date),
                        );
                    }}
                />
                {isStartNow ? (
                    <Box
                        sx={{
                            paddingLeft: '5px',
                            paddingRight: '40px',
                            color: theme => theme.palette.blue.main,
                        }}
                    >
                        <Localized
                            de="Sofort"
                            fr="Immédiatement"
                            it="Subito"
                            en="Immediately"
                        />
                    </Box>
                ) : (
                    <GuestReservationTimeDropdowns
                        name={GuestReservationAddField.startDateTime}
                        control={control}
                        minDateTime={now}
                        maxDateTime={now.plus({
                            days: maxDaysPurchaseAheadInFuture,
                        })}
                        rules={{
                            required: {
                                value: true,
                                message: mandatoryFieldMessage,
                            },
                        }}
                    />
                )}
            </Box>
        </Box>
    );
}

export function GuestReservationTimeDropdowns<
    TFieldValues extends FieldValues,
>({
    name,
    control,
    minDateTime,
    maxDateTime,
    rules,
}: {
    name: Path<TFieldValues>;
    control: Control<TFieldValues>;
    minDateTime: DateTime;
    maxDateTime: DateTime;
    rules?: Omit<
        RegisterOptions<TFieldValues>,
        'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
    >;
}) {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({
                field: { value, onChange },
                fieldState: { error, isTouched },
            }) => {
                const selectedDateTime = value ? DateTime.fromISO(value) : null;
                const timeSelectionOptions = getTimeSelectionOptions(
                    selectedDateTime,
                    minDateTime,
                    maxDateTime,
                );

                return (
                    <Box>
                        <Stack
                            spacing={2}
                            direction={'row'}
                            sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                            }}
                        >
                            <DesktopDatePicker
                                format="dd.MM.yyyy"
                                value={selectedDateTime}
                                minDate={minDateTime}
                                maxDate={maxDateTime}
                                onChange={d => {
                                    if (!d) return;
                                    const options = getTimeSelectionOptions(
                                        d,
                                        minDateTime,
                                        maxDateTime,
                                    ).map(o => o.value);
                                    const prevTime = d.set({
                                        hour: selectedDateTime?.hour,
                                        minute: selectedDateTime?.minute,
                                    });
                                    const prevTimeOption = options.find(
                                        o =>
                                            ensureIso(o) == ensureIso(prevTime),
                                    );
                                    onChange(
                                        ensureIso(
                                            prevTimeOption
                                                ? prevTimeOption
                                                : options[0],
                                        ),
                                    );
                                }}
                                slotProps={{
                                    textField: {
                                        size: 'medium',
                                        error: error && !isTouched,
                                    },
                                }}
                            />

                            <Select
                                onChange={e =>
                                    onChange(e.target.value as string)
                                }
                                value={value}
                                error={error && !isTouched}
                                sx={{ width: theme => theme.spacing(12) }}
                            >
                                {timeSelectionOptions.map(dateTime => (
                                    <MenuItem
                                        key={dateTime.text}
                                        value={ensureIso(dateTime.value)}
                                    >
                                        {dateTime.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                        {error && !isTouched && (
                            <FormHelperText error>
                                {error.message}
                            </FormHelperText>
                        )}
                    </Box>
                );
            }}
        />
    );
}

export function GuestReservationAddSuccess() {
    const { contractExternalId } = useParams<{ contractExternalId: string }>();
    const { activeFilters, searchText } = useGuestProductFilterConfiguration();
    const operatorContext = useOperatorContext();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const backUrl = `..${makeSearchQueryWithFilters(
        searchText,
        activeFilters,
    )}`;

    if (!contractExternalId) {
        return <Navigate to={'..'} />;
    }
    return (
        <GuestCardDownloadDialog
            open
            onDownload={() => navigate(backUrl)}
            onCancel={() => navigate(backUrl)}
            defaultLanguage={language}
            operatorId={operatorContext.currentLogin.mandantId}
            contractExternalId={contractExternalId}
        />
    );
}

function getTimeSelectionOptions(
    selectedDate: DateTime | null,
    minDateTime: DateTime,
    maxDateTime: DateTime,
) {
    if (!selectedDate) {
        return [];
    }
    const baseDate = selectedDate.startOf('day');
    const timeOptions = [
        '00:00',
        '01:00',
        '02:00',
        '03:00',
        '04:00',
        '05:00',
        '06:00',
        '07:00',
        '08:00',
        '09:00',
        '10:00',
        '11:00',
        '12:00',
        '13:00',
        '14:00',
        '15:00',
        '16:00',
        '17:00',
        '18:00',
        '19:00',
        '20:00',
        '21:00',
        '22:00',
        '23:00',
        '23:59',
    ];

    return timeOptions
        .filter(option => {
            const [hour, minute] = option.split(':').map(Number);
            const timeCandidate = baseDate.set({ hour, minute });
            return timeCandidate >= minDateTime && timeCandidate <= maxDateTime;
        })
        .map(option => {
            const [hour, minute] = option.split(':').map(Number);
            const value = baseDate.set({ hour, minute });
            return { value, text: option };
        });
}

enum GuestReservationAddField {
    contractTemplateId = 'contractTemplateId',
    isStartNow = 'isStartNow',
    startDateTime = 'startDateTime',
    endDateTime = 'endDateTime',
    tenantRemark = 'tenantRemark',
}

interface GuestReservationAddFieldValues {
    [GuestReservationAddField.contractTemplateId]: number;
    [GuestReservationAddField.isStartNow]: boolean;
    [GuestReservationAddField.startDateTime]: string;
    [GuestReservationAddField.endDateTime]: string;
    [GuestReservationAddField.tenantRemark]: string;
}

interface CreateGuestReservationPayload {
    contractTemplateId: number;
    isStartNow: boolean;
    startDateTime: string;
    endDateTime: string;
    tenantRemark: string;
}

interface CreateGuestReservationResponse {
    contractExternalId: string;
}
